// Webpack imports
import '@/styles/main.scss'

import Vue from 'vue'
import axios from 'axios'

// Plugins
// import Fragment from 'vue-fragment'
// Vue.use(Fragment.Plugin)
import VueCompositionApi from '@vue/composition-api'
Vue.use(VueCompositionApi)

import hooks from '@u3u/vue-hooks'
Vue.use(hooks)

import VueAware from 'vue-aware'
Vue.use(VueAware)

import VueSvgSprite from 'vue-svg-sprite'
Vue.use(VueSvgSprite, { url: '' })

// Components

// Directives
import VueOutside from '@/inc/directives/outside'
Vue.directive('outside', VueOutside)
import VueSrc from '@/inc/directives/src'
Vue.directive('src', VueSrc)
import VueAccordion from '@/inc/directives/accordion'
Vue.directive('accordion', VueAccordion)

import VueEnterViewport from '@/inc/directives/enter-viewport'
Vue.use(VueEnterViewport, {
  rootMargin: '0px 0px 0px 0px',
})

// Filters
import VFilters, {
  VFiltersOptions,
  capitalize,
  linebreak,
  modifiers,
  slugify,
  trimzeros,
} from '@monofront/v-filters'

Vue.use<VFiltersOptions>(VFilters, {
  capitalize,
  linebreak,
  modifiers,
  slugify,
  trimzeros,
})

import { langDefault } from '@/inc/app.config'
import i18n from '@/inc/i18n'
import { App, AppType } from '@/inc/types'
import { logger, pascalToKebab } from '@/inc/utils'

logger.info(process.env)

const isApiRe = new RegExp(process.env.VUE_APP_API_PATH as string)

// Flag every call made to the CMS (SSR and client side)
// Registered at module scope so SSR does not stack one interceptor per request
axios.interceptors.request.use(config => {
  if (isApiRe.test(config.url as string)) {
    config.headers['X-Axios'] = '1'
  }

  return config
})

Vue.prototype.$logger = logger
Vue.prototype.$debug = false
Vue.config.productionTip = false

// Import all global components
// Btn.vue => <g-btn></g-btn>
const requireComponent = require.context('@/components/g', true, /.*\.vue$/)

requireComponent.keys().forEach(fileName => {
  let baseComponentConfig = requireComponent(fileName)

  baseComponentConfig = baseComponentConfig.default || baseComponentConfig

  const baseComponentName =
    baseComponentConfig.name ||
    `g-${pascalToKebab(fileName.replace(/^.+\//, '').replace(/\.\w+$/, ''))}`

  Vue.component(baseComponentName, baseComponentConfig)
})

import Curtain from '@/components/Curtain.vue'
import IconLink from '@/components/IconLink.vue'
Vue.component('Curtain', Curtain)
Vue.component('IconLink', IconLink)

import gsap from 'gsap'
import InertiaPlugin from '@/inc/vendor/gsap/InertiaPlugin'
import Draggable from '@/inc/vendor/gsap/Draggable'
import SplitText from '@/inc/vendor/gsap/SplitText'
import ScrollTrigger from '@/inc/vendor/gsap/ScrollTrigger'
import CustomEase from '@/inc/vendor/gsap/CustomEase'

// Export factory function
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export default function createApp(ctx: any): App {
  const { type } = ctx as { type: AppType }

  logger.trace('[main:createApp]', type)

  if (type === 'client') {
    // Set default base URL
    axios.defaults.baseURL = window.location.origin
    Vue.prototype.$debug = window.location.search.includes('debug')

    // GSAP Plugins registration and defaults
    // Only on client side, else vapper throw error
    gsap.registerPlugin(Draggable)
    gsap.registerPlugin(InertiaPlugin)
    gsap.registerPlugin(SplitText)
    gsap.registerPlugin(ScrollTrigger)
    gsap.registerPlugin(CustomEase)
  }

  // Set default language
  axios.defaults.headers.common['Accept-Language'] = langDefault

  // Send ENV cookies to API (staging / branching)
  if (type === 'client' && process.env.VUE_APP_RELEASE === 'staging') {
    axios.interceptors.request.use(config => {
      const { epic_env_back: envBackCookie } = ctx.$cookie.getCookies()

      if (envBackCookie && isApiRe.test(config.url as string)) {
        logger.warn('ENV', config.url, envBackCookie)
        config.headers.cookie = `epic_env=${envBackCookie}`
      }

      return config
    })
  }

  /* eslint-disable @typescript-eslint/no-var-requires, global-require */
  const AppComponent = require('@/App.vue').default
  const ErrorComponent = require('@/Error.vue').default
  const createRouter = require('@/inc/router').default
  const createStore = require('@/inc/store').default
  /* eslint-enable @typescript-eslint/no-var-requires, global-require */

  const store = createStore(type)
  const router = createRouter(store)
  const app = {
    ErrorComponent,
    i18n,
    router,
    store,
    components: {
      Curtain,
      IconLink,
    },
    // This is necessary, it is for vue-meta
    head: {
      titleTemplate: '%s',
    },
    // This will expose `$root.$options.$resource` into components (server-side)
    $resource: ctx.$resource,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    render: (h: any) => h(AppComponent),
  }

  return app
}
